import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import ImageWithFallback from '../../components/ImageWithFallback';
import { getCourses, getMediaUrl } from '../../services/api';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';

const Blog = () => {
  const navigate = useNavigate();
  const [coursesList, setCoursesList] = useState([]);

  useEffect(() => {
    const fetchBlogCourses = async () => {
      try {
        const res = await getCourses();
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          setCoursesList(res.data);
        }
      } catch (err) {
        console.error("Failed to load blog courses:", err);
      }
    };
    fetchBlogCourses();
  }, []);

  const fallbackPosts = [
    {
      id: 15,
      title: 'Electric vehicle charging operation and maintenance',
      category: 'Electric Vehicle',
      lastUpdated: '26 Aug 2026',
      instructor: 'Dr. Preetranjan Sahoo',
      image: '/img/course-placeholder.png',
      description: 'Complete technical breakdown of EV charging operation, AC/DC fast chargers, battery management systems, and safety protocols for EV technicians.'
    },
    {
      id: 1,
      title: 'Mastering EV Battery Management Systems: A Beginner Guide',
      category: 'Electric Vehicle',
      lastUpdated: '18 Aug 2026',
      instructor: 'Debasish Majumdar',
      image: '/img/course-placeholder.png',
      description: 'Learn how modern lithium battery packs are monitored, thermal managed, and balanced in EV charging operation.'
    },
    {
      id: 2,
      title: 'Full Stack Development with MERN & PostgreSQL',
      category: 'Software Engineering',
      lastUpdated: '12 Aug 2026',
      instructor: 'Miss Prangya P Sahoo',
      image: '/img/course-placeholder.png',
      description: 'Build scalable web applications with React, Node.js, Express, and PostgreSQL relational database architectures.'
    }
  ];

  const displayPosts = coursesList.length > 0 ? coursesList : fallbackPosts;

  const handlePostClick = (post) => {
    navigate(`/courseoverview?course_id=${post.id}&title=${encodeURIComponent(post.title)}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-14 sm:pt-16">
      <Nav />
      <main className="flex-1 max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5 w-full space-y-3.5 sm:space-y-5">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-emerald-900 rounded-xl sm:rounded-2xl p-3.5 sm:py-4 sm:px-6 md:py-4 md:px-6 text-white text-center space-y-1.5 shadow-xs">
          <span className="bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
            Knowledge & Insights
          </span>
          <h1 className="text-sm sm:text-lg md:text-xl font-black leading-snug">E-Learning Hub Blog & Course Articles</h1>
          <p className="text-emerald-100/90 text-[11px] sm:text-xs max-w-xl mx-auto leading-snug">
            Explore engineering guides, technical masterclasses, course highlights, and career tips.
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {displayPosts.map((post) => (
            <div
              key={post.id}
              onClick={() => handlePostClick(post)}
              className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-2xs hover:shadow-md transition-all flex flex-col cursor-pointer group"
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
                <ImageWithFallback
                  src={getMediaUrl(post.image)}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-3.5 sm:p-4 flex-1 flex flex-col justify-between space-y-3">
                <div className="space-y-1.5">
                  <span className="text-[9px] font-extrabold text-emerald-700 uppercase bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    {post.category || 'Course Guide'}
                  </span>
                  <h3 className="text-xs sm:text-sm font-bold text-gray-900 group-hover:text-emerald-600 transition-colors leading-snug line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-[11px] text-gray-500 leading-normal line-clamp-2">{post.description || post.snippet}</p>
                </div>

                <div className="pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
                  <div className="flex items-center gap-1 font-medium truncate max-w-[60%]">
                    <User size={12} className="text-emerald-600 shrink-0" />
                    <span className="truncate">{post.instructor || post.author || 'Instructor'}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Calendar size={12} />
                    <span>{post.lastUpdated || post.date || '2026'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
